// Scheduling. FSRS handles the "I actually still know this" case far better
// than SM-2 does, which is most of the French collection.

import {
  fsrs,
  generatorParameters,
  Rating,
  State,
  type Card as FsrsCard,
  type Grade,
} from "ts-fsrs";
import type { Card, Direction, Lang, LangProfile, Note, Review, ReviewItem } from "./types";

export type RatingValue = 1 | 2 | 3 | 4;

export const RATING_LABELS: Record<RatingValue, string> = {
  1: "Again",
  2: "Hard",
  3: "Good",
  4: "Easy",
};

function engine(profile: LangProfile) {
  return fsrs(
    generatorParameters({
      request_retention: profile.requestRetention,
      // Ten years. A word recalled easily three times running does not need to
      // come back inside two years, and capping lower just manufactures reviews.
      maximum_interval: 3650,
      enable_fuzz: true,
      enable_short_term: profile.shortTermSteps,
    }),
  );
}

function toFsrs(card: Card): FsrsCard {
  return {
    due: new Date(card.due),
    stability: card.stability,
    difficulty: card.difficulty,
    elapsed_days: card.elapsed_days,
    scheduled_days: card.scheduled_days,
    learning_steps: card.learning_steps,
    reps: card.reps,
    lapses: card.lapses,
    state: card.state as State,
    last_review: card.last_review ? new Date(card.last_review) : undefined,
  };
}

export interface GradeResult {
  card: Card;
  review: Review;
  /** Set when this answer pushed the card past the leech threshold. */
  becameLeech: boolean;
}

export function gradeCard(
  card: Card,
  rating: RatingValue,
  profile: LangProfile,
  now = new Date(),
  durationMs?: number,
): GradeResult {
  const { card: next } = engine(profile).next(toFsrs(card), now, rating as unknown as Grade);

  // A card that keeps lapsing poisons the queue; suspend it and let the browse
  // screen surface it for rewriting rather than drilling a card that does not work.
  const becameLeech =
    next.lapses >= profile.leechThreshold && card.lapses < profile.leechThreshold;

  const updated: Card = {
    ...card,
    due: next.due.getTime(),
    stability: next.stability,
    difficulty: next.difficulty,
    elapsed_days: next.elapsed_days,
    scheduled_days: next.scheduled_days,
    learning_steps: next.learning_steps,
    reps: next.reps,
    lapses: next.lapses,
    state: next.state,
    last_review: now.getTime(),
    suspended: becameLeech ? 1 : card.suspended,
    updated_at: now.getTime(),
  };

  const review: Review = {
    id: crypto.randomUUID(),
    card_id: card.id,
    lang: card.lang,
    rating,
    state_before: card.state,
    reviewed_at: now.getTime(),
    duration_ms: durationMs ?? null,
  };

  return { card: updated, review, becameLeech };
}

/** Interval previews for the four answer buttons, so the UI can label them. */
export function previewIntervals(card: Card, profile: LangProfile, now = new Date()): Record<RatingValue, string> {
  const preview = engine(profile).repeat(toFsrs(card), now);
  const out = {} as Record<RatingValue, string>;
  for (const rating of [Rating.Again, Rating.Hard, Rating.Good, Rating.Easy]) {
    const item = preview[rating as Grade];
    out[rating as RatingValue] = formatInterval(item.card.due.getTime() - now.getTime());
  }
  return out;
}

function formatInterval(ms: number): string {
  const minutes = ms / 60_000;
  if (minutes < 60) return `${Math.max(1, Math.round(minutes))}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = hours / 24;
  if (days < 30) return `${Math.round(days)}d`;
  const months = days / 30.44;
  if (months < 12) return `${Math.round(months)}mo`;
  return `${(days / 365.25).toFixed(1)}y`;
}

// --- Queue building ---------------------------------------------------------

export interface DailyCounts {
  date: string;
  newIntroduced: number;
  reviewed: number;
}

export function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function freshCounts(): DailyCounts {
  return { date: today(), newIntroduced: 0, reviewed: 0 };
}

export interface QueueOptions {
  lang: Lang;
  profile: LangProfile;
  counts: DailyCounts;
  /** Restrict to one source — the "cram this episode" mode. */
  sourceId?: string | null;
  /** Restrict to one domain, e.g. mining terminology before a meeting. */
  domain?: string | null;
  now?: number;
}

const isNew = (card: Card) => card.reps === 0 && card.state === State.New;

/** How long a note's second direction waits after the first was answered. */
const SIBLING_COOLDOWN_MS = 12 * 60 * 60 * 1000;

/**
 * Build the session queue.
 *
 * One queue per language across every source. An episode is a tag, not a
 * container: scoping review per episode would drill today's material and never
 * show week three's again, which is exactly what spaced repetition exists to
 * prevent. `sourceId` narrows it deliberately, for cramming on listening day.
 */
export function buildQueue(
  cards: Card[],
  notes: Note[],
  options: QueueOptions,
): ReviewItem[] {
  const { lang, profile, counts } = options;
  const now = options.now ?? Date.now();
  const noteById = new Map(notes.map((n) => [n.id, n]));

  const eligible: ReviewItem[] = [];
  for (const card of cards) {
    if (card.lang !== lang || card.suspended) continue;
    const note = noteById.get(card.note_id);
    if (!note || note.status !== "active") continue;
    if (options.sourceId && note.source_id !== options.sourceId) continue;
    if (options.domain && note.domain !== options.domain) continue;
    eligible.push({ card, note });
  }

  const due = eligible
    .filter((item) => !isNew(item.card) && item.card.due <= now)
    .sort((a, b) => a.card.due - b.card.due);

  // A note's two directions are not introduced on the same day — seeing the
  // production card minutes after the recognition card tests memory of the last
  // thirty seconds, not of the word. That holds across sessions too, so a
  // sibling answered earlier today blocks its partner until tomorrow.
  const preferred: Direction = profile.productionFirst ? "production" : "recognition";
  const newByNote = new Map<string, ReviewItem[]>();
  const siblingSeenAt = new Map<string, number>();
  for (const item of eligible) {
    if (isNew(item.card)) {
      newByNote.set(item.note.id, [...(newByNote.get(item.note.id) ?? []), item]);
    } else if (item.card.last_review) {
      siblingSeenAt.set(
        item.note.id,
        Math.max(siblingSeenAt.get(item.note.id) ?? 0, item.card.last_review),
      );
    }
  }

  const fresh: ReviewItem[] = [];
  for (const [noteId, siblings] of newByNote) {
    const seen = siblingSeenAt.get(noteId);
    if (seen !== undefined && now - seen < SIBLING_COOLDOWN_MS) continue;
    if (siblings.length === 1) {
      fresh.push(siblings[0]!);
      continue;
    }
    fresh.push(siblings.find((s) => s.card.direction === preferred) ?? siblings[0]!);
  }
  fresh.sort((a, b) => a.note.created_at - b.note.created_at);

  const newAllowance = Math.max(0, profile.newPerDay - counts.newIntroduced);
  const selectedNew = fresh.slice(0, newAllowance);
  const totalAllowance = Math.max(0, profile.maxPerDay - counts.reviewed);
  const selectedDue = due.slice(0, Math.max(0, totalAllowance - selectedNew.length));

  return interleave(selectedDue, selectedNew);
}

/**
 * Spread new cards through the due reviews rather than front-loading them, so a
 * session does not open with twenty unknown words.
 */
function interleave(due: ReviewItem[], fresh: ReviewItem[]): ReviewItem[] {
  if (fresh.length === 0) return due;
  if (due.length === 0) return fresh;
  const out: ReviewItem[] = [];
  const gap = Math.max(1, Math.floor(due.length / fresh.length));
  let f = 0;
  for (let i = 0; i < due.length; i++) {
    out.push(due[i]!);
    if ((i + 1) % gap === 0 && f < fresh.length) out.push(fresh[f++]!);
  }
  while (f < fresh.length) out.push(fresh[f++]!);
  return out;
}

/**
 * Notes with something outstanding right now — due, or eligible to be
 * introduced. Shared by every count on the dashboard so the numbers there
 * always agree with the session buildQueue would actually produce.
 */
export function outstandingNotes(cards: Card[], notes: Note[], lang: Lang, now = Date.now()): Set<string> {
  const noteById = new Map(notes.map((n) => [n.id, n]));
  const ready = new Set<string>();
  const cooling = new Set<string>();
  for (const card of cards) {
    if (card.lang !== lang || card.suspended) continue;
    const note = noteById.get(card.note_id);
    if (!note || note.status !== "active") continue;
    if (isNew(card)) {
      ready.add(card.note_id);
      continue;
    }
    if (card.last_review && now - card.last_review < SIBLING_COOLDOWN_MS) {
      cooling.add(card.note_id);
    }
    if (card.due <= now) ready.add(card.note_id);
  }
  for (const noteId of cooling) {
    // Only a note whose sole remaining work is a cooling sibling drops out; a
    // genuinely due card keeps the note in the list.
    const hasDue = cards.some(
      (c) => c.note_id === noteId && !c.suspended && !isNew(c) && c.due <= now,
    );
    if (!hasDue) ready.delete(noteId);
  }
  return ready;
}

/**
 * Counts for the dashboard. `fresh` counts notes rather than cards, matching
 * what buildQueue will actually introduce — a note's two directions are never
 * introduced on the same day, so counting cards would over-promise the session.
 */
export function queueSummary(cards: Card[], notes: Note[], lang: Lang, now = Date.now()) {
  const noteById = new Map(notes.map((n) => [n.id, n]));
  const freshNotes = new Set<string>();
  const cooling = new Set<string>();
  let dueCount = 0;
  let total = 0;
  let suspended = 0;
  for (const card of cards) {
    if (card.lang !== lang) continue;
    const note = noteById.get(card.note_id);
    if (!note || note.status !== "active") continue;
    if (card.suspended) {
      suspended++;
      continue;
    }
    total++;
    if (isNew(card)) {
      freshNotes.add(card.note_id);
    } else {
      if (card.last_review && now - card.last_review < SIBLING_COOLDOWN_MS) {
        cooling.add(card.note_id);
      }
      if (card.due <= now) dueCount++;
    }
  }
  for (const noteId of cooling) freshNotes.delete(noteId);
  return { due: dueCount, fresh: freshNotes.size, total, suspended };
}
